import { useCallback, useMemo } from 'react';
import { pathOr } from 'ramda';
import moment from 'moment';
import { exportAndDownloadCSV } from '../../utils/csv-helpers';
import { exportAndDownloadPDF } from '../../utils/pdf-helper';
import { generateStandardFilename } from '@fortress-technology-solutions/fortress-component-library/utils';

const BASE_HEADERS = [
  { id: 'paymentNumber', label: '#' },
  { id: 'unit', label: 'Unit' },
  { id: 'householdStatus', label: 'Household Status' },
  { id: 'resident', label: 'Resident' },
  { id: 'transactionCode', label: 'Transaction Code' },
  { id: 'documentNumber', label: 'Doc #' },
  { id: 'amount', label: 'Amount' },
  { id: 'note', label: 'Note' },
];

const ADJUSTMENTS_HEADER = { id: 'adjustments', label: 'Adjustments' };

const BATCH_SUMMARY_HEADERS = [
  { id: 'batchName', label: 'Batch' },
  { id: 'batchDate', label: 'Batch Date' },
  { id: 'status', label: 'Status' },
];

// exportAndDownloadCSV does not print appendix header labels, so the first
// appendix row carries them.
const BATCH_SUMMARY_LABEL_ROW = Object.fromEntries(
  BATCH_SUMMARY_HEADERS.map(({ id, label }) => [
    id,
    { variant: 'string', value: label },
  ]),
);

const buildExportRows = (payments, propertyTransactionCodes) =>
  payments.map((payment, index) => {
    const code = propertyTransactionCodes.find(
      (transactionCode) =>
        transactionCode.id === payment.propertyTransactionCodeId,
    );
    return {
      paymentNumber: { variant: 'number', value: index + 1 },
      unit: {
        variant: 'string',
        value: pathOr('', ['unit', 'number'], payment),
      },
      householdStatus: {
        variant: 'string',
        value: pathOr('', ['householdStatus'], payment),
      },
      resident: {
        variant: 'string',
        value: pathOr('', ['receivedFromHouseholdMember'], payment),
      },
      transactionCode: {
        variant: 'string',
        value: pathOr('', ['transactionCode', 'code'], code),
      },
      documentNumber: {
        variant: 'string',
        value: pathOr('', ['documentNumber'], payment),
      },
      amount: {
        variant: 'currency',
        value: pathOr(0, ['transactionAmount'], payment),
      },
      note: {
        variant: 'string',
        value: pathOr(
          pathOr('', ['note'], payment),
          ['transactionNote'],
          payment,
        ),
      },
      adjustments: {
        variant: 'string',
        value: payment.reversed ? 'Yes' : '',
      },
    };
  });

const usePaymentBatchExport = ({
  batch,
  batchName,
  batchStatus,
  propertyTransactionCodes,
  isPosted,
}) => {
  const payments = useMemo(() => pathOr([], ['transactions'], batch), [batch]);

  const filteredHeaders = useMemo(
    () => (isPosted ? [...BASE_HEADERS, ADJUSTMENTS_HEADER] : BASE_HEADERS),
    [isPosted],
  );

  const rows = useMemo(
    () => buildExportRows(payments, propertyTransactionCodes),
    [payments, propertyTransactionCodes],
  );

  const batchDate = pathOr(null, ['header', 'batchDate'], batch);
  const formattedBatchDate = batchDate
    ? moment(batchDate).format('MM/DD/YYYY')
    : '';

  const onCSVButtonClick = useCallback(() => {
    const fileName = generateStandardFilename({
      type: 'payment-batch',
      descriptor: batchName,
      extension: 'csv',
    });
    const batchSummaryRow = {
      batchName: { variant: 'string', value: batchName },
      batchDate: { variant: 'string', value: formattedBatchDate },
      status: { variant: 'string', value: batchStatus ?? '' },
    };

    exportAndDownloadCSV({
      excludedHeaders: [],
      filteredHeaders,
      fileName,
      rows,
      appendixHeaders: BATCH_SUMMARY_HEADERS,
      appendixRows: [BATCH_SUMMARY_LABEL_ROW, batchSummaryRow],
      hasAnyFilters: false,
    });
  }, [filteredHeaders, rows, batchName, formattedBatchDate, batchStatus]);

  const onPDFButtonClick = useCallback(() => {
    const fileName = generateStandardFilename({
      type: 'payment-batch',
      descriptor: batchName,
      extension: 'pdf',
    });

    exportAndDownloadPDF({
      excludedHeaders: [],
      filteredHeaders,
      fileName,
      rows,
      hasAnyFilters: false,
      title: `Payment Batch: ${batchName}`,
      subtitle: formattedBatchDate ? `Batch Date: ${formattedBatchDate}` : '',
    });
  }, [filteredHeaders, rows, batchName, formattedBatchDate]);

  const hasPayments = payments.length > 0;

  return { onCSVButtonClick, onPDFButtonClick, hasPayments };
};

export default usePaymentBatchExport;
